import React, { FC, useState } from "react"
import { Form, Table } from "react-bootstrap"
import { useSelector } from "react-redux"
import { Field as FormikField } from "formik"

import { getSettlementMethod, canEdit, getSettings } from "@/onboarding/features/product/selectors"
import { ReservedFields, SettlementMethods } from "@/onboarding/features/product/models/Product"
import { useModal } from "@/helpers/useModal"
import EditModal from "@/onboarding/features/product/components/shared/EditModal"
import EditIcon from "@/onboarding/features/product/components/shared/EditIcon"
import FormikFieldSubmitter from "@/onboarding/features/product/components/shared/FormikFieldSubmitter"
import FormikBootstrap from "@/components/formik-bootstrap"
import CruiseLineOnly from "@/auth_descriptor/CruiseLineOnly"

const displayName = (settlementMethod: SettlementMethods) => {
  switch (settlementMethod) {
    case SettlementMethods.PER_GUEST:
      return "Fixed price per guest"
    case SettlementMethods.PER_VEHICLE:
      return "Tiered per-vehicle occupancy"
    case SettlementMethods.PER_VEHICLE_AVERAGE:
      return "Tiered based on average vehicle occupancy"
    default:
      return "Unknown type"
  }
}

const description = (settlementMethod: SettlementMethods) => {
  switch (settlementMethod) {
    case SettlementMethods.PER_GUEST:
      return "Settle using the agreed upon net rate per guest ticket collected. Tiered rates are calculated according to total tickets."
    case SettlementMethods.PER_VEHICLE:
      return "Settle using the agreed upon net rate per vehicle guest count. Vehicles are filled up one by one up to capacity. Tiered rates are calculated according to guests per vehicle."
    case SettlementMethods.PER_VEHICLE_AVERAGE:
      return "Settle using the agreed upon net rate per vehicle guest count. Guest counts are averaged across all vehicles. Tiered rates are calculated according to guests per vehicle."
    default:
      return "Unknown type"
  }
}

const Edit: FC = () => {
  const settlementMethod = useSelector(getSettlementMethod)
  const availableMethods = [
    SettlementMethods.PER_GUEST,
    SettlementMethods.PER_VEHICLE,
    SettlementMethods.PER_VEHICLE_AVERAGE,
  ]

  return (
    <FormikFieldSubmitter initialValues={{ [ReservedFields.SETTLEMENT_METHOD]: settlementMethod }}>
      <FormikBootstrap.Select name={ReservedFields.SETTLEMENT_METHOD}>
        {availableMethods.map((method) => (
          <option key={method} value={method}>
            {displayName(method)}
          </option>
        ))}
      </FormikBootstrap.Select>
      <FormikField name={ReservedFields.SETTLEMENT_METHOD}>
        {({ field: { value } }) => <Form.Text muted>{description(value)}</Form.Text>}
      </FormikField>
    </FormikFieldSubmitter>
  )
}

const SettlementMethod: FC = () => {
  const { settlement_method } = useSelector(getSettings(ReservedFields.SETTLEMENT_METHOD))
  const previousSettlementMethod = settlement_method.previousValue
  const currentSettlementMethod = useSelector(getSettlementMethod)

  const editable = useSelector(canEdit)

  const toggleModal = useModal((props) => (
    <EditModal {...props} title="Settlement Method" Body={Edit} />
  ))

  const [showPrevious, setShowPrevious] = useState(false)

  const handleToggle = () => {
    if (settlement_method?.changed) {
      setShowPrevious(!showPrevious)
    }
  }

  const textStyle = showPrevious ? "original-product-text" : ""
  const labelStyle = settlement_method?.changed ? "yellow-underline" : ""

  return (
    <Table>
      <thead>
        <tr>
          <th>
            <div
              onClick={handleToggle}
              style={{ cursor: settlement_method?.changed ? "pointer" : "default" }}
            >
              <span className={`me-3 ${labelStyle}`}>Settlement Method</span>
              <CruiseLineOnly>{editable && <EditIcon onEdit={toggleModal} />}</CruiseLineOnly>
              {settlement_method?.changed && (
                <small className="ms-2 text-muted fst-italic">
                  {showPrevious ? "Original" : ""}
                </small>
              )}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={textStyle}>
            {showPrevious && settlement_method?.changed
              ? displayName(previousSettlementMethod)
              : displayName(currentSettlementMethod)}
          </td>
        </tr>
      </tbody>
    </Table>
  )
}

export default SettlementMethod
